/**
 * Central overlay-display configuration — ONE switchboard for the multi-tier
 * catalog overlay system (system default + customer / org / tenant / user / team
 * overlays) across every resource that carries overlays.
 *
 * The backend always resolves the FULL precedence ladder (see
 * `commons/catalog/precedence.py`); this config governs only how much of it the
 * dashboard SURFACES, per resource, so the product can roll the tiers out
 * incrementally:
 *
 *   - `resolvedView`  — show the read-only "effective overlays" card at all.
 *   - `showProvenance` — annotate each winning chip with which tier supplied it.
 *   - `showLogos`      — use the overlay's own logo on chips / avatars / graph.
 *   - `resolveScopes`  — the WIDEN ("resolve at …") tiers offered as one-click
 *                        re-resolution. Ordered; a subset of the address ladder.
 *   - `writeScopes`    — the visibility tiers offered when ATTACHING an overlay.
 *
 * To light up org / customer overlays later, add the tier to `resolveScopes` /
 * `writeScopes` here — no component changes. To ship a resource dark, flip
 * `enabled:false`.
 */
import type { AddressScope } from "@authe/fe-commons/graphql";

/** Every resource that can carry the catalog overlay stack. */
export type OverlayResource = "transaction" | "address" | "entity" | "label" | "tag" | "group";

/**
 * Visibility tier chosen when WRITING (attaching) an overlay. Distinct from the
 * read-time {@link AddressScope} widen ladder: this is who the new assignment is
 * visible to. Maps onto the commons ABAC carriers in the attach handlers:
 *   - `private` → `user_scoped` (owner partition, no grants)
 *   - `team`    → owner partition + `visible_to_team_ids`
 *   - `tenant`  → tenant partition (everyone in the org)
 *   - `org` / `customer` → wider partitions (future; require elevated scope)
 */
export type OverlayWriteScope = "private" | "team" | "tenant" | "org" | "customer";

export interface OverlayResourceConfig {
  /** Master switch for the whole overlay surface on this resource. */
  enabled: boolean;
  /** Render the read-only precedence-RESOLVED ("effective") overlays view. */
  resolvedView: boolean;
  /** Annotate winning chips with their provenance tier (dot + tooltip). */
  showProvenance: boolean;
  /** Use the overlay's own logo (falls back to a colour swatch / initial). */
  showLogos: boolean;
  /** Widen ("resolve at …") tiers offered on the resolved view. Ordered. */
  resolveScopes: AddressScope[];
  /** Visibility tiers offered on the attach control. Ordered. */
  writeScopes: OverlayWriteScope[];
  /**
   * The visibility tier a NEW attach defaults to when the caller doesn't pick
   * one — the write-scope analog of a default. Drives the attach control's
   * initial state, the graph right-click attaches (which offer no picker), and
   * the auto-attach that fires when an address-bound catalog row is created.
   * Change here to move the whole product from tenant-wide to private (or, once
   * lit up, org / customer) without touching a single call site. Must be one of
   * this resource's {@link writeScopes}.
   */
  defaultWriteScope: OverlayWriteScope;
  /**
   * The tenancy tier a RESOLVED overlay view opens at when the caller hasn't
   * widened — the read-time analog of {@link defaultWriteScope}. `USER` is the
   * caller's own view (their personal overlays on top of the shared tiers, the
   * current behavior); a wider tier drops personal overlays and reads as that
   * tier sees it. Drives the initial state of the "resolve at …" switcher and
   * the default scope `useResolvedAddressOverlays` reads at. Must be one of this
   * resource's {@link resolveScopes}.
   */
  defaultResolveScope: AddressScope;
}

/**
 * Phase-1 rollout: system default + tenant + user, everywhere. Org / customer
 * tiers are intentionally omitted from `resolveScopes` / `writeScopes` — add
 * them here (e.g. `["USER", "TENANT", "ORG", "CUSTOMER", "GLOBAL"]`) to enable
 * the wider ladder without touching any component.
 */
const PHASE_1_RESOLVE_SCOPES: AddressScope[] = ["USER", "TENANT", "GLOBAL"];
const PHASE_1_WRITE_SCOPES: OverlayWriteScope[] = ["private", "team", "tenant"];

/**
 * Phase-1 default attach visibility. `tenant` means a newly attached overlay (or
 * an auto-attached address-bound row) is visible to everyone in the org — the
 * sensible default for shared investigation. Flip to `"private"` here to make
 * new attaches user-private by default across the whole app.
 */
const PHASE_1_DEFAULT_WRITE_SCOPE: OverlayWriteScope = "tenant";

/**
 * Phase-1 default resolve tier. `USER` = the caller's own view (personal
 * overlays on top of the shared tiers), preserving today's read behavior. Flip
 * to `"TENANT"` here to open every resolved view at the tenant tier by default.
 */
const PHASE_1_DEFAULT_RESOLVE_SCOPE: AddressScope = "USER";

function phase1(overrides: Partial<OverlayResourceConfig> = {}): OverlayResourceConfig {
  return {
    enabled: true,
    resolvedView: true,
    showProvenance: true,
    showLogos: true,
    resolveScopes: PHASE_1_RESOLVE_SCOPES,
    writeScopes: PHASE_1_WRITE_SCOPES,
    defaultWriteScope: PHASE_1_DEFAULT_WRITE_SCOPE,
    defaultResolveScope: PHASE_1_DEFAULT_RESOLVE_SCOPE,
    ...overrides,
  };
}

/**
 * The per-resource overlay switchboard. Each surface reads its own resource's
 * entry via {@link overlayConfig} so transactions, addresses, entities and the
 * catalog kinds can advance through the rollout independently.
 */
export const OVERLAY_CONFIG: Record<OverlayResource, OverlayResourceConfig> = {
  // Transactions already receive a fully-resolved payload (with alternatives)
  // from `transactions-mgmt-api`, so the widen ladder is safe to show in full.
  transaction: phase1(),
  address: phase1(),
  entity: phase1(),
  label: phase1(),
  tag: phase1(),
  group: phase1(),
};

/** The overlay config for `resource` (never throws — falls back to disabled). */
export function overlayConfig(resource: OverlayResource): OverlayResourceConfig {
  return OVERLAY_CONFIG[resource] ?? DISABLED_OVERLAY_CONFIG;
}

/** Convenience: is the overlay surface on for this resource at all? */
export function isOverlayEnabled(resource: OverlayResource): boolean {
  return overlayConfig(resource).enabled;
}

/** A fully-dark config for unknown resources / hard kill-switch. */
export const DISABLED_OVERLAY_CONFIG: OverlayResourceConfig = {
  enabled: false,
  resolvedView: false,
  showProvenance: false,
  showLogos: false,
  resolveScopes: [],
  writeScopes: ["private"],
  defaultWriteScope: "private",
  defaultResolveScope: "USER",
};

/**
 * The backend ABAC carriers a catalog attach is written under. The single
 * translation from an {@link OverlayWriteScope} to the address-mgmt attach
 * request fields, so no call site re-derives `user_scoped` inline:
 *
 *   - `private` → `user_scoped` (owner partition, no grants).
 *   - `team`    → `user_scoped` + `visible_to_team_ids` (owner + those teams).
 *   - `tenant` / `org` / `customer` → tenant-wide (ownerless); org / customer
 *     widening is a future backend capability, so today they collapse to the
 *     tenant partition (the widest the attach endpoint writes).
 *
 * `team` with no `teamIds` degrades to a plain private attach rather than a
 * tenant-wide one (a team share with no teams is a private note, not public).
 */
export interface AttachScopeArgs {
  user_scoped: boolean;
  visible_to_team_ids?: string[];
}

export function writeScopeToAttachArgs(
  scope: OverlayWriteScope,
  opts: { teamIds?: string[] } = {},
): AttachScopeArgs {
  const teamIds = opts.teamIds ?? [];
  if (scope === "team") {
    return teamIds.length > 0
      ? { user_scoped: true, visible_to_team_ids: teamIds }
      : { user_scoped: true };
  }
  if (scope === "private") return { user_scoped: true };
  // tenant / org / customer → tenant-wide, ownerless.
  return { user_scoped: false };
}

/**
 * The resources that actually take a write/attach visibility default — the ones
 * a user creates or attaches an overlay for. Transactions carry no write path
 * (their tenancy is a read/view concept), so they're intentionally excluded from
 * the user-editable default-visibility picker.
 */
export const WRITE_SCOPE_RESOURCES: OverlayResource[] = [
  "address",
  "entity",
  "label",
  "tag",
  "group",
];

/**
 * The effective default write scope for `resource`: the caller's own saved
 * override when it's still a tier this resource allows, else the product default
 * from {@link OVERLAY_CONFIG}. Pure (no storage / React) so it's trivially
 * testable and shared by both the provider and any non-React caller. Validating
 * against `writeScopes` means a stale saved value (e.g. after the rollout narrows
 * the offered tiers) safely falls back instead of attaching at an unoffered tier.
 */
export function resolveWriteScope(
  overrides: Partial<Record<OverlayResource, OverlayWriteScope>> | null | undefined,
  resource: OverlayResource,
): OverlayWriteScope {
  const config = overlayConfig(resource);
  const override = overrides?.[resource];
  if (override && config.writeScopes.includes(override)) return override;
  return config.defaultWriteScope;
}

/**
 * The resources whose RESOLVED overlay view has a `AddressScope` widen ladder —
 * the catalog/address family. Transactions are excluded here: they resolve their
 * tenancy tier through the explorer's own `TxScopeId` feed scope (see
 * `features/transactions/scopes.ts`), not this ladder.
 */
export const RESOLVE_SCOPE_RESOURCES: OverlayResource[] = [
  "address",
  "entity",
  "label",
  "tag",
  "group",
];

/**
 * The effective default resolve tier for `resource`: the caller's saved override
 * when it's still a tier this resource offers (`resolveScopes`), else the product
 * default. Pure mirror of {@link resolveWriteScope} for the read axis, so a stale
 * saved value safely falls back instead of resolving at an unoffered tier.
 */
export function resolveResolveScope(
  overrides: Partial<Record<OverlayResource, AddressScope>> | null | undefined,
  resource: OverlayResource,
): AddressScope {
  const config = overlayConfig(resource);
  const override = overrides?.[resource];
  if (override && config.resolveScopes.includes(override)) return override;
  return config.defaultResolveScope;
}
